<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { IPrinter } from '@/interfaces/IPrinter';
  import { useV2PrintingUi } from '@/composition/printing/use-printing-v2-ui';
  import { CODEPAGE_CYCLE, buildCodepageSample } from '@/lib/print/test-ticket';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { IWizardDevice } from './StepDevice.vue';

  const props = defineProps<{
    device: IWizardDevice;
    format: 'escpos' | 'zpl';
    paperWidthMm: 58 | 80;
    codepage: string;
  }>();

  const emit = defineEmits<{
    (e: 'confirmed'): void;
    (e: 'codepage-change', codepage: string): void;
  }>();

  const ui = useUi();
  const {
    bridge,
    isConnected,
    printing,
    printingStore,
    printTestDocument,
  } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  type TPrintState = 'idle' | 'printing' | 'done' | 'failed';
  const printState = ref<TPrintState>('idle');
  const printError = ref<string | null>(null);

  /** After a successful print, the human answers "¿Salió bien?". */
  const trouble = ref<'none' | 'symbols' | 'nothing'>('none');

  const printerLike = computed(() => ({
    ref: props.device.ref,
    name: props.device.name,
    format: props.format,
  }));

  async function printTest() {
    if (printState.value === 'printing') return;
    printState.value = 'printing';
    printError.value = null;
    trouble.value = 'none';
    try {
      // The printer is UNSAVED at this step: override the resolved defaults
      // with the wizard's in-flight paper/codepage choices.
      await printTestDocument(printerLike.value, i18nT('printingV2.kindTest'), {
        escpos: {
          codepage: props.codepage,
          paperWidthMm: props.paperWidthMm,
        },
      });
      printState.value = 'done';
    } catch (e) {
      console.error('[PrintingV2] wizard test print failed', e);
      printState.value = 'failed';
      printError.value = e instanceof Error ? e.message : null;
    }
  }

  // #region trouble: símbolos raros (codepage cycle)

  const printingSamples = ref(false);
  const samplesPrinted = ref(false);
  const savingSample = ref(false);

  async function printSamples() {
    if (printingSamples.value) return;
    printingSamples.value = true;
    try {
      const printer: IPrinter = {
        id: props.device.ref,
        name: props.device.name,
        type: 'usb',
      };
      // Resolved cut mode (existing entry by ref, or the 'modern'
      // default for a brand-new device): on a legacy-cut printer the
      // samples must still separate.
      const { escpos } = printingStore.printerSettingsOf(printer);
      for (let i = 0; i < CODEPAGE_CYCLE.length; i++) {
        const bytes = buildCodepageSample(
          i + 1,
          CODEPAGE_CYCLE[i],
          escpos.cutMode
        );
        await printing.printByteArray(printer, bytes, {
          kind: 'test',
          label: `${i18nT('printingV2.kindTest')} ${i + 1}`,
        });
      }
      samplesPrinted.value = true;
      ui.showToast(AlertType.Success, i18nT('printingV2.helpSamplesSent'));
    } catch (e) {
      console.error('[PrintingV2] wizard samples failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      printingSamples.value = false;
    }
  }

  async function pickSample(index: number) {
    savingSample.value = true;
    try {
      emit('codepage-change', CODEPAGE_CYCLE[index]);
      samplesPrinted.value = false;
      trouble.value = 'none';
      printState.value = 'idle';
      ui.showToast(AlertType.Success, i18nT('printingV2.helpCodepageSaved'));
    } finally {
      savingSample.value = false;
    }
  }

  // #endregion

  // #region trouble: no salió nada (probe)

  const probing = ref(false);
  const probeResult = ref<'ok' | 'fail' | null>(null);

  async function probe() {
    if (!bridge.value || probing.value) return;
    probing.value = true;
    probeResult.value = null;
    try {
      const result = await bridge.value.probePrinter(props.device.ref);
      probeResult.value = result.status === 'online' ? 'ok' : 'fail';
    } catch (e) {
      probeResult.value = 'fail';
    } finally {
      probing.value = false;
    }
  }

  // #endregion
</script>

<template>
  <div class="wizard-step-test">
    <div class="test-printer-line">
      <PosUiIcon name="printer" class="test-printer-icon" />
      {{ device.name }}
    </div>

    <!-- Print action + state -->
    <div class="test-main">
      <PosUiButton
        color="primary"
        size="lg"
        icon="printer"
        :loading="printState === 'printing'"
        :disabled="!isConnected"
        @click="printTest"
      >
        {{ $t('printingV2.testPrintButton') }}
      </PosUiButton>

      <div v-if="printState === 'failed'" class="test-result is-fail">
        <PosUiIcon name="close-circle" class="test-result-icon" />
        <span>
          {{ $t('printingV2.testFailed') }}
          <template v-if="printError"> — {{ printError }}</template>
        </span>
      </div>
    </div>

    <!-- Human confirmation (mandatory before finishing) -->
    <div
      v-if="printState === 'done' && trouble === 'none'"
      class="test-question"
    >
      <div class="test-question-title">{{ $t('printingV2.testQuestion') }}</div>
      <div class="test-question-actions">
        <PosUiButton color="success" icon="check" @click="emit('confirmed')">
          {{ $t('printingV2.testYes') }}
        </PosUiButton>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="close"
          @click="trouble = format === 'escpos' ? 'symbols' : 'nothing'"
        >
          {{ $t('printingV2.testNo') }}
        </PosUiButton>
      </div>
    </div>

    <!-- Trouble flows -->
    <div
      v-if="trouble !== 'none' || printState === 'failed'"
      class="test-trouble"
    >
      <div v-if="printState === 'done'" class="test-trouble-tabs">
        <button
          v-if="format === 'escpos'"
          type="button"
          class="trouble-tab"
          :class="{ active: trouble === 'symbols' }"
          @click="trouble = 'symbols'"
        >
          {{ $t('printingV2.troubleSymbols') }}
        </button>
        <button
          type="button"
          class="trouble-tab"
          :class="{ active: trouble === 'nothing' }"
          @click="trouble = 'nothing'"
        >
          {{ $t('printingV2.troubleNothing') }}
        </button>
      </div>

      <!-- Codepage cycle -->
      <div
        v-if="trouble === 'symbols' && printState === 'done'"
        class="trouble-body"
      >
        <p class="trouble-text">{{ $t('printingV2.helpSymbolsIntro') }}</p>
        <PosUiButton
          color="primary"
          variant="soft"
          size="sm"
          :loading="printingSamples"
          :disabled="!isConnected"
          @click="printSamples"
        >
          {{ $t('printingV2.helpPrintSamples') }}
        </PosUiButton>
        <template v-if="samplesPrinted">
          <p class="trouble-text">{{ $t('printingV2.helpPickSample') }}</p>
          <div class="trouble-actions">
            <PosUiButton
              v-for="(cp, index) in CODEPAGE_CYCLE"
              :key="cp"
              color="neutral"
              variant="outline"
              size="sm"
              :loading="savingSample"
              @click="pickSample(index)"
            >
              {{ $t('printingV2.helpSampleN', { n: index + 1 }) }}
            </PosUiButton>
          </div>
        </template>
      </div>

      <!-- Connection check -->
      <div
        v-if="trouble === 'nothing' || printState === 'failed'"
        class="trouble-body"
      >
        <ol class="trouble-steps">
          <li>{{ $t('printingV2.helpNoPrintStep1') }}</li>
          <li>{{ $t('printingV2.helpNoPrintStep2') }}</li>
        </ol>
        <div class="trouble-actions">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            :loading="probing"
            :disabled="!isConnected"
            @click="probe"
          >
            {{ $t('printingV2.helpProbe') }}
          </PosUiButton>
        </div>
        <div v-if="probeResult === 'ok'" class="test-result is-ok">
          <PosUiIcon name="check-circle" class="test-result-icon" />
          {{ $t('printingV2.helpProbeOk') }}
        </div>
        <div v-else-if="probeResult === 'fail'" class="test-result is-fail">
          <PosUiIcon name="close-circle" class="test-result-icon" />
          {{ $t('printingV2.helpProbeFail') }}
        </div>

        <!-- Escape hatch (failed send only): finish setup now, fix later. -->
        <div v-if="printState === 'failed'" class="trouble-continue">
          <p class="trouble-text">
            {{ $t('printingV2.testContinueHint') }}
          </p>
          <PosUiButton
            color="warning"
            variant="soft"
            size="sm"
            @click="emit('confirmed')"
          >
            {{ $t('printingV2.continueAnyway') }}
          </PosUiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .wizard-step-test {
    @apply tw-flex tw-flex-col tw-gap-4;

    .test-printer-line {
      @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-neutral-700;

      .test-printer-icon {
        @apply tw-text-lg tw-text-neutral-400;
      }
    }

    .test-main {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-3;
    }

    .test-result {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm;

      &.is-ok {
        @apply tw-bg-emerald-50 tw-text-emerald-700;
      }

      &.is-fail {
        @apply tw-bg-red-50 tw-text-red-700;
      }

      .test-result-icon {
        @apply tw-flex-none tw-text-base;
      }
    }

    .test-question {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-4;

      .test-question-title {
        @apply tw-mb-3 tw-text-sm tw-font-semibold tw-text-neutral-800;
      }

      .test-question-actions {
        @apply tw-flex tw-gap-2;
      }
    }

    .test-trouble {
      @apply tw-flex tw-flex-col tw-gap-3;

      .test-trouble-tabs {
        @apply tw-flex tw-gap-0.5 tw-self-start tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

        .trouble-tab {
          @apply tw-rounded-md tw-border tw-border-transparent tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-text-neutral-800;

          &.active {
            @apply tw-border-neutral-200 tw-bg-white tw-font-semibold tw-text-neutral-900 tw-shadow-sm;
          }
        }
      }

      .trouble-body {
        @apply tw-flex tw-flex-col tw-items-start tw-gap-2.5;

        .trouble-text {
          @apply tw-m-0 tw-text-sm tw-text-neutral-600;
        }

        .trouble-steps {
          @apply tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-text-neutral-600;
        }

        .trouble-actions {
          @apply tw-flex tw-flex-wrap tw-gap-2;
        }

        .trouble-continue {
          @apply tw-mt-1 tw-flex tw-flex-col tw-items-start tw-gap-2 tw-border-t tw-border-neutral-100 tw-pt-3;

          .trouble-text {
            @apply tw-m-0 tw-text-sm tw-text-neutral-600;
          }
        }
      }
    }
  }
</style>
